import { uuid } from "@/utils";
import type { WaitMatchAddress, MatchStreetDoubleAddressResult } from "./type";
import OrderDTO from "@/dto/order/OrderDTO";
import { LoggerInfoTag, postLogInfo } from "../api/log";
import DoubleAddressCheckWorker from "./worker?worker&inline";

let worker: globalThis.Worker | null = null;

const resolveMap = new Map<
    string,
    (
        value:
            | Record<string, MatchStreetDoubleAddressResult>
            | PromiseLike<Record<string, MatchStreetDoubleAddressResult>>,
    ) => void
>();

/**
 * 获取双地址检测worker进程
 */
function getDoubleAddressCheckerWorker() {
    if (worker) {
        return worker;
    }

    worker = new DoubleAddressCheckWorker();

    const onMessage = (event: MessageEvent<{ id: string; data: Record<string, MatchStreetDoubleAddressResult> }>) => {
        const id = event.data?.id;
        const resolve = resolveMap.get(id);

        if (resolve) {
            resolve(event.data?.data);
        }
    };

    worker.addEventListener("message", onMessage);

    return worker;
}

function joinTownAndStreet(town: any, street: any) {
    if (!town) {
        return street;
    }

    if (!street) {
        return town;
    }

    // 部分平台在street重复添加town
    if (street.indexOf(town) === 0) {
        return street;
    }

    return town + street;
}

const logMap = new Map<string, boolean>();

/**
 * 回传双地址的订单地址省市区信息
 */
function log(addresses: WaitMatchAddress[], result: Record<string, MatchStreetDoubleAddressResult>) {
    try {
        const matched = Object.values(result).filter((it) => !!it.isMatch);

        let texts: string[] = [];
        for (let i = 0; i < matched.length; i++) {
            const match = matched[i];

            const hasLog = logMap.get(match.id);
            if (match.isMatch && !hasLog) {
                logMap.set(match.id, true);

                const address = addresses.find((it) => it.id === matched[i].id);

                if (match.localStreetRangeInfo) {
                    texts.push(
                        `${address.localProvince}${address.localCity}${address.localDistrict}${address.localStreet}`,
                    );
                } else {
                    texts.push(`${address.province}${address.city}${address.district}${address.street}`);
                }
            }
        }
        const content = texts.join(",");

        if (content) {
            postLogInfo({
                tag: LoggerInfoTag.LOCAL_DOUBLE_ADDRESS,
                content,
            });
        }
    } catch (error) {}
}

/**
 * 匹配订单双地址信息
 */
export async function matchDoubleAddresses(orders: Pick<OrderDTO, "consignee" | "shopId" | "id">[]) {
    if (!orders) return {};
    const worker = getDoubleAddressCheckerWorker();

    const addresses: WaitMatchAddress[] = orders
        .map((order) => {
            if (!order?.consignee) {
                return null;
            }

            const province = order.consignee.province;
            const city = order.consignee.city;
            const district = order.consignee.district;
            const street = joinTownAndStreet(order.consignee.town, order.consignee.street);
            const localProvince = order.consignee.localProvince;
            const localCity = order.consignee.localCity;
            const localDistrict = order.consignee.localDistrict;
            const localStreet = order.consignee.localStreet;

            return {
                id: `${order?.shopId}_${order?.id}`,
                province,
                city,
                district,
                street,
                localProvince,
                localCity,
                localDistrict,
                localStreet,
            };
        })
        .filter((it) => !!it);

    const result = await new Promise<Record<string, MatchStreetDoubleAddressResult>>((resolve) => {
        const id = uuid();

        resolveMap.set(id, resolve);

        worker.postMessage({
            id,
            data: addresses,
        });
    });

    log(addresses, result);

    return result;
}
